// =============================================================================
// ppiModeloQuestoes — gera o .xlsx MODELO do banco de questões do PPI
// =============================================================================
// Por que existe: a planilha de questões chega do Treinamento e é importada como
// está. Quando o cabeçalho vem com nome diferente (ou falta o `resposta_correta`),
// a carga recusa a linha e alguém perde a manhã descobrindo por quê. O modelo
// entrega o cabeçalho EXATO que o importador entende, já com exemplo ao lado.
//
// ⚠️ A ORDEM DAS ABAS IMPORTA. O importador lê SEMPRE a PRIMEIRA aba
// (`wb.Sheets[wb.SheetNames[0]]`, em `server/treinamento/ppi/server-ppi-prova.js`).
// Por isso a 1ª aba vem **só com o cabeçalho**: se alguém subir o modelo sem
// preencher, a carga lê 0 linhas e nada de lixo entra no banco. O exemplo e as
// instruções ficam nas abas 2 e 3, que o importador ignora.
//
// ⚠️ Os nomes de coluna aqui têm de bater com os que o parser aceita
// (`server/treinamento/ppi/ppi-questoes-import.js` › `valor(...)`). Mexeu lá,
// mexa aqui — são as duas pontas do mesmo contrato.

import * as XLSX from 'xlsx';

// O cabeçalho, na ordem em que a planilha do Treinamento sempre veio.
export const COLUNAS_MODELO = [
    'id', 'area', 'subarea', 'nivel', 'tipo', 'enunciado',
    'alternativa_a', 'alternativa_b', 'alternativa_c', 'alternativa_d', 'alternativa_e',
    'resposta_correta', 'explicacao', 'pontuacao', 'area_critica', 'status', 'codigo_guia',
];

// Duas linhas de exemplo — uma de múltipla escolha simples, uma de situação
// prática com 5 alternativas — para mostrar o formato sem precisar de manual.
const EXEMPLOS = [
    {
        id: 'Q001', area: 'Liderança', subarea: 'Foco no Cliente', nivel: 'Médio',
        tipo: 'Múltipla Escolha',
        enunciado: 'Durante o pico do almoço, o tempo de atendimento do Drive-Thru sobe. Qual é a primeira ação do gerente de plantão?',
        alternativa_a: 'Chamar o gerente da loja para assumir o posicionamento.',
        alternativa_b: 'Reposicionar a equipe conforme o gargalo observado na rota de inspeção.',
        alternativa_c: 'Reduzir o cardápio disponível até o fluxo normalizar.',
        alternativa_d: 'Aguardar o fim do pico e tratar o problema na reunião do turno.',
        alternativa_e: '',
        resposta_correta: 'B',
        explicacao: 'O posicionamento é responsabilidade do plantão e se ajusta durante o turno, não depois dele.',
        pontuacao: 2, area_critica: 'Não', status: 'Ativa', codigo_guia: 'LID001',
    },
    {
        id: 'Q002', area: 'Segurança dos Alimentos', subarea: 'Zona de Perigo', nivel: 'Difícil',
        tipo: 'Situação Prática',
        enunciado: 'Um produto do UHC ultrapassou o tempo de validade secundária. Qual é o procedimento correto?',
        alternativa_a: 'Reaquecer o produto e reiniciar a contagem.',
        alternativa_b: 'Servir o produto apenas em pedidos de delivery.',
        alternativa_c: 'Descartar o produto e registrar a perda.',
        alternativa_d: 'Transferir o produto para outro gabinete do UHC.',
        alternativa_e: 'Consultar o gerente antes de decidir.',
        resposta_correta: 'C',
        explicacao: 'Validade secundária vencida é descarte imediato — não há reaproveitamento.',
        pontuacao: 3, area_critica: 'Sim', status: 'Ativa', codigo_guia: 'SEG014',
    },
];

// O que cada coluna significa. Vira a aba "Instruções" — é o manual junto do
// arquivo, que é onde quem preenche realmente procura.
const INSTRUCOES = [
    ['Coluna', 'Obrigatória?', 'O que preencher'],
    ['id', 'Recomendada', 'Código da questão (Q001, Q002…). É a chave de atualização: re-subir a planilha com o mesmo id CORRIGE a questão em vez de duplicar. Os códigos reiniciam a cada trilha — não tem problema.'],
    ['area', 'SIM', 'Grande tema (Liderança, Produção, Área Drive-Thru…). ⚠️ Grafia diferente cria ÁREA NOVA e parte o estoque em dois — copie o nome exato de uma área que já existe.'],
    ['subarea', 'Não', 'Detalhamento livre dentro da área. Não é usada no sorteio.'],
    ['nivel', 'Recomendada', 'Fácil · Médio · Difícil · Avançado. É por ele que a prova é sorteada ("15% difícil, 35% médio…"). Sem nível, a questão fica fora desses pedidos.'],
    ['tipo', 'Não', 'Múltipla Escolha · Situação Prática · Interpretação · Tomada de Decisão. Só descritivo.'],
    ['enunciado', 'SIM', 'A pergunta. É o texto que identifica a questão quando não há id.'],
    ['alternativa_a … alternativa_e', 'Mínimo 2', 'Deixe em branco as que não usar. A ordem das letras é preservada.'],
    ['resposta_correta', 'SIM', 'A LETRA do gabarito (A a E). ⚠️ Apontar para uma alternativa em branco reprova a linha inteira.'],
    ['explicacao', 'Não', 'Justificativa do gabarito. Aparece na correção.'],
    ['pontuacao', 'Não', 'Peso da questão na prova. Em branco = 1.'],
    ['area_critica', 'Não', 'Sim / Não.'],
    ['status', 'Não', '"Inativa" entra fora do sorteio. Em branco ou "Ativa" = disponível.'],
    ['codigo_guia', 'Não', 'Referência ao material de origem (SAL001, SEG014…).'],
    ['', '', ''],
    ['COMO IMPORTAR', '', '1) Preencha a aba "Questoes" (é a única que o sistema lê). 2) Escolha a trilha nesta tela. 3) Importar planilha.'],
    ['RE-SUBIR NÃO DUPLICA', '', 'Questão que ainda não caiu em prova é ATUALIZADA. A que já caiu é PRESERVADA — mexer nela mudaria o ranking de quem já respondeu.'],
];

// Larguras de coluna: sem isso o enunciado nasce cortado e a planilha parece vazia.
const LARGURAS = { id: 10, area: 22, subarea: 20, nivel: 12, tipo: 20, enunciado: 70, resposta_correta: 16, explicacao: 55, pontuacao: 11, area_critica: 13, status: 10, codigo_guia: 13 };

// Monta e dispara o download do arquivo. Nome fixo — o usuário renomeia depois.
export function baixarModeloQuestoes() {
    const larguras = COLUNAS_MODELO.map((c) => ({ wch: LARGURAS[c] || 34 }));

    // Aba 1 — a que o importador lê: SÓ o cabeçalho.
    const aba = XLSX.utils.json_to_sheet([], { header: COLUNAS_MODELO });
    aba['!cols'] = larguras;

    // Aba 2 — o mesmo cabeçalho, preenchido, para copiar e colar.
    const abaExemplo = XLSX.utils.json_to_sheet(EXEMPLOS, { header: COLUNAS_MODELO });
    abaExemplo['!cols'] = larguras;

    // Aba 3 — o manual de cada coluna.
    const abaInstrucoes = XLSX.utils.aoa_to_sheet(INSTRUCOES);
    abaInstrucoes['!cols'] = [{ wch: 30 }, { wch: 14 }, { wch: 110 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, aba, 'Questoes');
    XLSX.utils.book_append_sheet(wb, abaExemplo, 'Exemplo');
    XLSX.utils.book_append_sheet(wb, abaInstrucoes, 'Instruções');
    XLSX.writeFile(wb, 'modelo-banco-questoes-ppi.xlsx');
}
